import { Suspense, lazy, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { PaletteProvider } from "@/contexts/PaletteContext";
import { VersionProvider } from "@/contexts/VersionContext";
import { AuthProvider } from "@/contexts/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useMediaPreviewHighlight } from "@/hooks/useMediaPreviewHighlight";

// Wrapper to enable Media Library "Open on Site" preview highlighting on all pages
const PreviewHighlightProvider = ({ children }: { children: ReactNode }) => {
  useMediaPreviewHighlight();
  return <>{children}</>;
};
const Index = lazy(() => import("./pages/Index"));

// Lazy load non-critical routes for better initial load performance
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Services = lazy(() => import("./pages/Services"));
const Auth = lazy(() => import("./pages/Auth"));
const MediaLibrary = lazy(() => import("./pages/MediaLibrary"));
const Discovery = lazy(() => import("./pages/Discovery"));
const Work = lazy(() => import("./pages/Work"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const CaseStudiesList = lazy(() => import("./pages/admin/CaseStudiesList"));
const CaseStudyBuilder = lazy(() => import("./pages/admin/CaseStudyBuilder"));
const ClientsList = lazy(() => import("./pages/admin/ClientsList"));
const ClientDetail = lazy(() => import("./pages/admin/ClientDetail"));
const ProposalGenerator = lazy(() => import("./pages/admin/ProposalGenerator"));
const ProposalsList = lazy(() => import("./pages/admin/ProposalsList"));
const CrmDashboard = lazy(() => import("./pages/admin/CrmDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Dpa = lazy(() => import("./pages/Dpa"));

// Careers pages
const Careers = lazy(() => import("./pages/Careers"));
const CareersJobs = lazy(() => import("./pages/CareersJobs"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const AdminCareersJobs = lazy(() => import("./pages/admin/AdminCareersJobs"));
const AdminJobEditor = lazy(() => import("./pages/admin/AdminJobEditor"));
const AdminApplications = lazy(() => import("./pages/admin/AdminApplications"));
const AdminTemplates = lazy(() => import("./pages/admin/AdminTemplates"));

// Signal Hub admin
const AdminSignal = lazy(() => import("./pages/admin/AdminSignal"));
const AdminSignalEditor = lazy(() => import("./pages/admin/AdminSignalEditor"));

// Dispatch Hub (public)
const DispatchHub = lazy(() => import("./pages/SignalPreview"));
const DispatchDetail = lazy(() => import("./pages/SignalPreviewDetail"));

// Redirect helper for legacy slug routes
const DispatchSlugRedirect = lazy(() => import("./components/SignalSlugRedirect"));

// Portal pages (client-facing)
const PortalOnboarding = lazy(() => import("./pages/portal/Onboarding"));
const PortalDashboard = lazy(() => import("./pages/portal/Dashboard"));

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <VersionProvider>
          <PaletteProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <Suspense fallback={<div className="min-h-screen bg-background" />}>
                  <PreviewHighlightProvider>
                    <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/discovery" element={<Discovery />} />
                    <Route path="/work" element={<Work />} />
                    <Route path="/work/:slug" element={<CaseStudyDetail />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/media-library" element={
                      <ProtectedRoute requireAdmin>
                        <MediaLibrary />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/case-studies" element={
                      <ProtectedRoute requireAdmin>
                        <CaseStudiesList />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/case-studies/new" element={
                      <ProtectedRoute requireAdmin>
                        <CaseStudyBuilder />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/case-studies/:id/edit" element={
                      <ProtectedRoute requireAdmin>
                        <CaseStudyBuilder />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/crm" element={
                      <ProtectedRoute requireAdmin>
                        <CrmDashboard />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/clients" element={
                      <ProtectedRoute requireAdmin>
                        <ClientsList />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/clients/:clientId" element={
                      <ProtectedRoute requireAdmin>
                        <ClientDetail />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/proposals" element={
                      <ProtectedRoute requireAdmin>
                        <ProposalGenerator />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/proposals/history" element={
                      <ProtectedRoute requireAdmin>
                        <ProposalsList />
                      </ProtectedRoute>
                    } />
                    
                    {/* Careers Admin Routes */}
                    <Route path="/admin/careers" element={
                      <ProtectedRoute requireAdmin>
                        <AdminCareersJobs />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/careers/new" element={
                      <ProtectedRoute requireAdmin>
                        <AdminJobEditor />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/careers/:id/edit" element={
                      <ProtectedRoute requireAdmin>
                        <AdminJobEditor />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/careers/applications" element={
                      <ProtectedRoute requireAdmin>
                        <AdminApplications />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/careers/templates" element={
                      <ProtectedRoute requireAdmin>
                        <AdminTemplates />
                      </ProtectedRoute>
                    } />
                    
                    {/* Signal Hub Admin Routes */}
                    <Route path="/admin/signal" element={
                      <ProtectedRoute requireAdmin>
                        <AdminSignal />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/signal/new" element={
                      <ProtectedRoute requireAdmin>
                        <AdminSignalEditor />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/signal/:id/edit" element={
                      <ProtectedRoute requireAdmin>
                        <AdminSignalEditor />
                      </ProtectedRoute>
                    } />
                    
                    {/* Dispatch Hub (public) */}
                    <Route path="/dispatch" element={<DispatchHub />} />
                    <Route path="/dispatch/:slug" element={<DispatchDetail />} />
                    {/* Legacy redirects */}
                    <Route path="/signal" element={<Navigate to="/dispatch" replace />} />
                    <Route path="/signal/:slug" element={<DispatchSlugRedirect />} />
                    <Route path="/signal-preview" element={<Navigate to="/dispatch" replace />} />
                    <Route path="/signal-preview/:slug" element={<DispatchSlugRedirect />} />
                    
                    {/* Public Careers Routes */}
                    <Route path="/careers" element={<Careers />} />
                    <Route path="/careers/jobs" element={<CareersJobs />} />
                    <Route path="/careers/job/:slug" element={<JobDetail />} />
                    
                    {/* Portal Routes (Client-facing) */}
                    <Route path="/portal" element={<PortalDashboard />} />
                    <Route path="/portal/onboarding" element={<PortalOnboarding />} />
                    
                    {/* Legal Pages */}
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/legal/dpa" element={<Dpa />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                    </Routes>
                  </PreviewHighlightProvider>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
          </PaletteProvider>
        </VersionProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
